import React, { useState, useEffect, useRef } from "react";
import { Box, TextField, Typography } from "@mui/material";
import CustomButton from "./common/CustomButton";
import { COLORS } from "../assets/theme";
import { deleteBindsApi, useSessionQuery } from "../services/AccountService";
import ToastNotification from "./ToastNotification";
import { telnetApi } from "../services/ProviderService";
import { hasJwtPermission } from "../services/ServicesHelpers";

interface TelnetDialogContentProps {
  id: string;
  rowType?: string;
  onExportClick?: () => void;
  resetPermissionScope?: "ACCOUNT" | "LIVE_GATEWAY";
}

function TelnetDialogContent({
  id,
  rowType,
  onExportClick,
  resetPermissionScope = "LIVE_GATEWAY",
}: TelnetDialogContentProps) {
  const [telnetLogs, setTelnetLogs] = useState("");
  const { data } = useSessionQuery(id);
  const textAreaRef = useRef<HTMLTextAreaElement>(null); 

  useEffect(() => {
    if (data && data.content) {
      setTelnetLogs(data.content.join("\n"));
    }
  }, [data]);

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.scrollTop = textAreaRef.current.scrollHeight;
    }
  }, [telnetLogs]);

  const handleTelnetClick = () => {
    try {
      telnetApi(id);
      ToastNotification.success("Telnet has been added.");
    } catch (error: any) {
      ToastNotification.error("Failed to add telnet.");
    }
  };

  const handleResetClick = async (id: string) => {
    try {
      await deleteBindsApi(id);
      ToastNotification.success("Connection has been reset.");
    } catch (error: any) {
      console.error("Failed to reset connection:", error);
      ToastNotification.error("Failed to reset connection.");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", mt: 2, width: "100%" }}>
     <Box
  sx={{
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: 1,
    justifyContent: "space-between",
  }}
>
  <CustomButton
    label="Telnet"
    onClick={handleTelnetClick}
    variant="outlined"
    size="medium"
    borderColor={COLORS.GOLD}
    textColor={COLORS.GOLD}
    disabled={rowType === "CLIENT" || (!hasJwtPermission(
      resetPermissionScope === "ACCOUNT"
        ? "RUN_TELNET_ACCOUNT"
        : "RUN_TELNET_LIVE_GATEWAY"
    ))}
  />
  <Box sx={{ display: "flex", gap: 1 }}>
    <CustomButton
      label="Reset Connection"
      onClick={() => handleResetClick(id)}
      variant="outlined"
      size="medium"
      borderColor={COLORS.GOLD}
      textColor={COLORS.GOLD}
      disabled={!hasJwtPermission(
        resetPermissionScope === "ACCOUNT"
          ? "RESET_CONNECTION_ACCOUNT"
          : "RESET_CONNECTION_LIVE_GATEWAY"
      )}
    />
    <CustomButton
      label="Export"
      onClick={onExportClick}
      variant="outlined"
      size="medium"
      borderColor={COLORS.GOLD}
      textColor={COLORS.GOLD}
      disabled={!hasJwtPermission(
        resetPermissionScope === "ACCOUNT"
          ? "GET_ALL_SESSION_STATE_LOGS_ACCOUNT"
          : "GET_ALL_SESSION_STATE_LOGS_LIVE_GATEWAY"
      )}
    />
  </Box>
</Box>

      <Typography variant="subtitle1" sx={{ color: "white", mb: 0 }}>
        Telnet Logs
      </Typography>

      <TextField
        variant="outlined"
        multiline
        rows={20}
        fullWidth
        value={telnetLogs}
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            padding: 0,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#333333",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#333333",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#333333",
          },
        }}
        InputProps={{
          sx: {
            fontFamily: "monospace",
            fontSize: "0.875rem",
          },
        }}
        inputProps={{
          readOnly: true,
          style: {
            backgroundColor: "#000",
            color: "#0f0",
            paddingLeft: "10px",
            paddingBottom: "40px",
            fontFamily: "monospace",
          },
        }}
        inputRef={textAreaRef}
      />
    </Box>
  );
}

export default TelnetDialogContent;